import { useState } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Typography,
  Alert,
  Stack,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BusinessIcon from '@mui/icons-material/Business';
import LanguageIcon from '@mui/icons-material/Language';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import BadgeIcon from '@mui/icons-material/Badge';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import InfoIcon from '@mui/icons-material/Info';
import RequestPageIcon from '@mui/icons-material/RequestPage';
import { fetchListingById, deleteListing } from '../api/services';
import { useFetch } from '../hooks';
import {
  LoadingSpinner,
  ErrorDisplay,
  ListingStatusChip,
  PermissionChip,
  RouteMap,
  MarkdownRenderer,
  FavoriteButton,
  ApplyToListingModal,
  LoginRequiredModal,
} from '../components/common';
import { DESIGN_TOKENS } from '../styles/theme';
import { useAuthentication, useNotifications } from '../store/AppContext';
import { resolveAssetUrl, formatDatePolish } from '../utils';

export default function ListingDetailPage() {
  const navigate = useNavigate();
  const { listingId } = useParams<{ listingId: string }>();
  const numericId = listingId ?? "";
  const { currentUser } = useAuthentication();
  const { showNotification } = useNotifications();
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isPilot = currentUser?.membership?.USER_TYPE === 'PILOT_B2B_COMPANY';

  const {
    data: listingResponse,
    isLoading,
    error,
    refetch,
  } = useFetch(
    () => fetchListingById(numericId),
    [numericId],
    { enabled: numericId != undefined }
  );

  const listing = listingResponse?.data;
  const isOwner = listing?.owner?.documentId === currentUser?.membership?.company_transport?.documentId;
  const isOpenListing = listing?.listing_status === 'OPEN';
  const isPrivateListingAccess = Boolean(listing && !isOpenListing);
  const ownerApplicationsPath =
    listing?.listing_status === 'CLOSED'
      ? `/account/orders/${listing.documentId}`
      : `/account/received-applications/${listing?.documentId ?? ''}`;

  const handleDownloadGeoJSON = () => {
    if (!listing?.basic_information?.route_geojson) return;

    const dataStr = JSON.stringify(listing.basic_information.route_geojson, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

    const exportFileDefaultName = `route_${listing.documentId}.geojson`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    showNotification('Pobieranie pliku GeoJSON rozpoczęte.', 'info');
  };

  const handleDelete = async () => {
    if (!listing) return;
    if (!window.confirm('Czy na pewno chcesz usunąć to ogłoszenie? Tej operacji nie można cofnąć.')) return;

    setIsDeleting(true);
    try {
      await deleteListing(listing.documentId);
      showNotification('Ogłoszenie zostało usunięte.', 'success');
      navigate('/account/my-listings');
    } catch (err: any) {
      showNotification(err.message || 'Wystąpił błąd podczas usuwania ogłoszenia.', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUnauthorizedApplyAttempt = () => {
    setIsLoginModalOpen(true);
  };

  if (isLoading) {
    return <LoadingSpinner message="Ładowanie ogłoszenia..." fullHeight />;
  }

  if (error || !listing) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <ErrorDisplay message={error ?? 'Nie znaleziono ogłoszenia.'} onRetry={refetch} />
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Button
            component={RouterLink}
            to="/listings"
            startIcon={<ArrowBackIcon />}
            variant="outlined"
          >
            Powrót do listy
          </Button>
        </Box>
      </Container>
    );
  }

  const { basic_information, listing_status, permissions, order_value, owner, pilots_quantity } = listing;

  return (
    <Box sx={{ backgroundColor: DESIGN_TOKENS.colors.background, minHeight: '80vh', py: 5 }}>
      <Container maxWidth="lg">
        <Button
          component={RouterLink}
          to="/listings"
          startIcon={<ArrowBackIcon />}
          variant="text"
          size="small"
          sx={{ mb: 3 }}
        >
          Powrót do listy ogłoszeń
        </Button>

        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: { xs: 3, md: 5 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <ListingStatusChip status={listing_status} />
              {order_value !== undefined && order_value !== null ? (
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h4" fontWeight={700} color={DESIGN_TOKENS.colors.accent}>
                    {order_value.toLocaleString('pl-PL')} PLN
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    wartość netto
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'right', maxWidth: '300px' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    W ogłoszeniu nie podano kwoty. Należy skontaktować się bezpośrednio z firmą w celu podjęcia negocjacji.
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h2" gutterBottom sx={{ mb: 0 }}>
                {basic_information.title}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                {isOwner && (
                  <>
                    <Button
                      component={RouterLink}
                      to={ownerApplicationsPath}
                      startIcon={<RequestPageIcon />}
                      variant="contained"
                      size="small"
                    >
                      {listing_status === 'OPEN' ? 'Nadesłane oferty' : 'Zobacz zlecenie'}
                    </Button>
                    <Button
                      component={RouterLink}
                      to={`/account/my-listings/edit/${listing.documentId}`}
                      startIcon={<EditIcon />}
                      variant="outlined"
                      size="small"
                    >
                      Edytuj
                    </Button>
                    <Button
                      startIcon={<DeleteIcon />}
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={handleDelete}
                      disabled={isDeleting}
                    >
                      Usuń
                    </Button>
                  </>
                )}
                <FavoriteButton listingDocumentId={listing.documentId} size="large" />
              </Stack>
            </Box>

            <Divider sx={{ my: 3 }} />

            {isPrivateListingAccess && (
              <Alert severity="info" sx={{ mb: 3 }}>
                {isOwner
                  ? 'To ogłoszenie nie jest już otwarte. Widzisz je, ponieważ Twoja firma jest jego właścicielem.'
                  : 'To ogłoszenie nie jest już otwarte. Widzisz je, ponieważ jesteś przypisanym pilotem.'}
              </Alert>
            )}

            <Grid container spacing={4}>
              <Grid item xs={12} md={7}>
                {permissions.length > 0 && (
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Wymagane uprawnienia
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {permissions.map((permission) => (
                        <PermissionChip key={permission} permission={permission} />
                      ))}
                    </Box>
                  </Box>
                )}

                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Liczba pilotów
                  </Typography>
                  <Typography variant="body1">
                    {pilots_quantity ?? 1}
                  </Typography>
                </Box>

                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Szczegóły dotyczące zlecenia
                </Typography>
                <Box sx={{ mb: 4 }}>
                  <MarkdownRenderer content={basic_information.description} />
                </Box>
              </Grid>

              <Grid item xs={12} md={5}>
                {owner && (
                  <Box
                    sx={{
                      p: 3,
                      mb: 3,
                      backgroundColor: DESIGN_TOKENS.colors.surface,
                      borderRadius: DESIGN_TOKENS.borderRadius.card,
                      border: `1px solid ${DESIGN_TOKENS.colors.border}`,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '12px',
                        border: `2px solid ${DESIGN_TOKENS.colors.border}`,
                        backgroundColor: DESIGN_TOKENS.colors.surface,
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                      }}
                    >
                      {owner.company_information?.brand_graphics?.logo?.url ? (
                        <Box
                          component="img"
                          src={resolveAssetUrl(owner.company_information.brand_graphics.logo.url)}
                          alt="Logo firmy"
                          sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                      ) : (
                        <BusinessIcon sx={{ fontSize: 32, color: DESIGN_TOKENS.colors.primaryLight }} />
                      )}
                    </Box>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      {owner.company_information?.company_data?.company_name ?? 'Firma transportowa'}
                    </Typography>

                    <Box sx={{ width: '100%', mt: 2, mb: 2, textAlign: 'left' }}>
                      {owner.company_information?.tax_information?.tax_identification_number && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <BadgeIcon sx={{ fontSize: 18, color: DESIGN_TOKENS.colors.primary }} />
                          <Typography variant="body2">
                            NIP: {owner.company_information.tax_information.tax_identification_number}
                          </Typography>
                        </Box>
                      )}
                      {owner.company_information?.contact_details?.website_url && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <LanguageIcon sx={{ fontSize: 18, color: DESIGN_TOKENS.colors.primary }} />
                          <Typography
                            variant="body2"
                            component="a"
                            href={owner.company_information.contact_details.website_url.startsWith('http')
                                  ? owner.company_information.contact_details.website_url
                                  : `https://${owner.company_information.contact_details.website_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ color: DESIGN_TOKENS.colors.primary, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                          >
                            {owner.company_information.contact_details.website_url.replace(/^https?:\/\//, '')}
                          </Typography>
                        </Box>
                      )}
                      {owner.company_information?.contact_details?.phone_number && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <PhoneIcon sx={{ fontSize: 18, color: DESIGN_TOKENS.colors.primary }} />
                          <Typography variant="body2" component="a" href={`tel:${owner.company_information.contact_details.phone_number}`} sx={{ color: 'inherit', textDecoration: 'none' }}>
                            {owner.company_information.contact_details.phone_number}
                          </Typography>
                        </Box>
                      )}
                      {owner.company_information?.contact_details?.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <EmailIcon sx={{ fontSize: 18, color: DESIGN_TOKENS.colors.primary }} />
                          <Typography variant="body2" component="a" href={`mailto:${owner.company_information.contact_details.email}`} sx={{ color: 'inherit', textDecoration: 'none' }}>
                            {owner.company_information.contact_details.email}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <Button
                      component={RouterLink}
                      to={`/companies/transport-companies/${owner.documentId}`}
                      variant="outlined"
                      size="small"
                      fullWidth
                    >
                      Zobacz profil firmy
                    </Button>
                  </Box>
                )}

                <Box
                  sx={{
                    p: 3,
                    backgroundColor: DESIGN_TOKENS.colors.primaryLighter,
                    borderRadius: DESIGN_TOKENS.borderRadius.card,
                    border: `1px solid ${DESIGN_TOKENS.colors.border}`,
                  }}
                >
                  {/* Date details */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                      Termin realizacji
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <CalendarTodayIcon color="primary" />
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          TERMIN REALIZACJI
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {basic_information.time_frame?.startDate ? formatDatePolish(basic_information.time_frame.startDate): 'Brak daty'}
                          {basic_information.time_frame?.endDate && ` — ${formatDatePolish(basic_information.time_frame.endDate)}`}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                    Szczegóły trasy
                  </Typography>

                  {/* Route details */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                      <LocationOnIcon color="primary" />
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          MIEJSCOWOŚĆ POCZĄTKOWA
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {basic_information.position_start?.city ?? basic_information.position_start?.address ?? 'Nie podano'}
                        </Typography>
                        {basic_information.position_start?.address && basic_information.position_start?.city && (
                          <Typography variant="caption">{basic_information.position_start.address}</Typography>
                        )}
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <LocationOnIcon color="primary" />
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          MIEJSCOWOŚĆ KOŃCOWA
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {basic_information.end_position?.city ?? basic_information.end_position?.address ?? 'Nie podano'}
                        </Typography>
                        {basic_information.end_position?.address && basic_information.end_position?.city && (
                          <Typography variant="caption">{basic_information.end_position.address}</Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  {basic_information.route_geojson && (
                    <Alert
                      severity="info"
                      icon={<InfoIcon fontSize="inherit" />}
                      sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        Wyświetlana jest szczegółowa trasa
                      </Typography>
                      <Typography variant="caption">
                        Trasa została wczytana z pliku GeoJSON dostarczonego przez ogłoszeniodawcę.
                      </Typography>
                    </Alert>
                  )}

                  <RouteMap
                    start={basic_information.position_start}
                    end={basic_information.end_position}
                    geojson={basic_information.route_geojson}
                  />

                  {basic_information.route_geojson && (
                    <Button
                      fullWidth
                      variant="outlined"
                      size="small"
                      startIcon={<CloudDownloadIcon />}
                      onClick={handleDownloadGeoJSON}
                      sx={{ mt: 2, mb: 1, textTransform: 'none' }}
                    >
                      Pobierz trasę (.geojson)
                    </Button>
                  )}

                  {basic_information.position_start?.lat && basic_information.end_position?.lat && (
                    <Button
                      fullWidth
                      variant="outlined"
                      size="small"
                      startIcon={<LocationOnIcon />}
                      href={`https://www.google.com/maps/dir/?api=1&origin=${basic_information.position_start.lat},${basic_information.position_start.lng}&destination=${basic_information.end_position.lat},${basic_information.end_position.lng}`}
                      target="_blank"
                      sx={{ mt: 1, textTransform: 'none' }}
                    >
                      Zobacz trasę w Google Maps
                    </Button>
                  )}
                </Box>

                {isPilot && isOpenListing && (
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    sx={{ mt: 3, py: 1.5 }}
                    onClick={() => setIsApplyModalOpen(true)}
                  >
                    Aplikuj na zlecenie
                  </Button>
                )}

                {!currentUser && isOpenListing && (
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    sx={{ mt: 3, py: 1.5 }}
                    onClick={handleUnauthorizedApplyAttempt}
                  >
                    Aplikuj na zlecenie
                  </Button>
                )}

                {isPilot && !isOpenListing && (
                  <Alert severity="info" sx={{ mt: 3 }}>
                    Aplikowanie jest dostępne tylko dla otwartych ogłoszeń.
                  </Alert>
                )}

                {!isPilot && currentUser && !isOwner && (
                  <Alert severity="info" sx={{ mt: 3 }}>
                    Tylko piloci mogą aplikować na zlecenia.
                  </Alert>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>

      <ApplyToListingModal
        open={isApplyModalOpen}
        onClose={() => setIsApplyModalOpen(false)}
        listingDocumentId={listing.documentId}
        listingTitle={basic_information.title}
      />

      <LoginRequiredModal
        open={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        title="Zaloguj się, aby aplikować na ogłoszenie"
        description="Aplikowanie na ogłoszenia jest dostępne wyłącznie dla zalogowanych użytkowników. Zaloguj się, aby wysłać zgłoszenie."
      />
    </Box>
  );
}
